import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-muted/30">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-display font-bold text-xl">PocketFiler</span>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-card rounded-lg shadow-sm border p-8 prose prose-slate dark:prose-invert max-w-none">
          <h1>Privacy Policy</h1>
          <p className="text-muted-foreground"><strong>Effective Date:</strong> March 17, 2026 &nbsp;|&nbsp; <strong>Version:</strong> 1.0</p>

          <h2>1. Introduction</h2>
          <p>Lucid Tech LLC ("Company," "we," "us," or "our") operates the PocketFiler platform (pocketfiler.com, app.pocketfiler.com, and api.pocketfiler.com). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our Platform.</p>

          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, password (stored as a one-way hash — we never store your actual password), preferred language, and account type</li>
            <li><strong>Organization Information:</strong> Organization name, type, billing email, and team member details</li>
            <li><strong>Contract Content:</strong> Text, terms, and details you provide or generate for contract creation</li>
            <li><strong>Documents:</strong> Files you upload, including contracts and supporting documents</li>
            <li><strong>Communication Content:</strong> Messages sent through the Platform's messaging features</li>
            <li><strong>Payment Information:</strong> Billing details processed through Stripe (we do not store full credit card numbers)</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Signature Audit Data:</strong> IP address, browser type and version, and precise timestamp for each electronic signature (required for legal compliance)</li>
            <li><strong>Usage Data:</strong> Pages visited, features used, and interaction patterns</li>
            <li><strong>Device Information:</strong> Browser type, operating system, and device type</li>
            <li><strong>Log Data:</strong> Server logs including IP addresses and access times</li>
            <li><strong>Cookies:</strong> Session cookies for authentication and preference cookies for language/UI settings. We do not use advertising cookies.</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <ul>
            <li><strong>Providing the Service:</strong> Account management, contract generation, signature processing, document storage</li>
            <li><strong>Authentication and Security:</strong> Identity verification, account security, fraud prevention</li>
            <li><strong>Signature Verification:</strong> Maintaining audit trails as required by the ESIGN Act and UETA</li>
            <li><strong>Payment Processing:</strong> Subscription payments and billing</li>
            <li><strong>Communication:</strong> Transactional emails (signature requests, notifications, password resets)</li>
            <li><strong>Platform Improvement:</strong> Analyzing usage to improve features and performance</li>
            <li><strong>Aggregated Analytics:</strong> Creating anonymized datasets to understand usage trends</li>
            <li><strong>Legal Compliance:</strong> Complying with applicable laws and regulations</li>
          </ul>

          <h2>4. Third-Party Service Providers</h2>
          <p>We share information with the following providers solely to operate the Platform:</p>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr><th>Provider</th><th>Purpose</th><th>Data Shared</th></tr>
              </thead>
              <tbody>
                <tr><td><strong>Amazon Web Services (AWS)</strong></td><td>Cloud infrastructure, hosting</td><td>All platform data (encrypted)</td></tr>
                <tr><td><strong>MongoDB Atlas</strong></td><td>Database hosting</td><td>Account data, contracts, signatures</td></tr>
                <tr><td><strong>Stripe, Inc.</strong></td><td>Payment processing</td><td>Name, email, payment details</td></tr>
                <tr><td><strong>Anthropic</strong></td><td>AI contract generation</td><td>Contract prompts (no PII beyond what you include)</td></tr>
                <tr><td><strong>Resend / Gmail SMTP</strong></td><td>Email delivery</td><td>Email addresses, email content</td></tr>
              </tbody>
            </table>
          </div>
          <p className="font-semibold">We do not sell your personal information to any third party.</p>

          <h2>5. Aggregated and Anonymized Data</h2>
          <p>We may create aggregated, de-identified datasets from platform usage that cannot reasonably identify you. This data may be used for product improvement, research, statistical reporting, and development of new services. <strong>We do not sell your personal data.</strong></p>

          <h2>6. Data Retention</h2>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr><th>Data Type</th><th>Retention Period</th></tr>
              </thead>
              <tbody>
                <tr><td>Account Information</td><td>Duration of account + 90 days</td></tr>
                <tr><td><strong>Electronic Signature Records</strong></td><td><strong>Minimum 7 years</strong></td></tr>
                <tr><td><strong>Signature Audit Trails</strong></td><td><strong>Minimum 7 years</strong></td></tr>
                <tr><td>Contracts and Documents</td><td>Duration of account + 90 days</td></tr>
                <tr><td>Payment Records</td><td>As required by law (typically 7 years)</td></tr>
                <tr><td>Usage Logs</td><td>12 months</td></tr>
                <tr><td>Aggregated Data</td><td>Indefinite (non-identifiable)</td></tr>
              </tbody>
            </table>
          </div>

          <h2>7. Your Privacy Rights</h2>
          <h3>7.1 All Users</h3>
          <p>You have the right to: access your personal information, correct inaccuracies, delete your account and data (subject to legal retention), export your data, and withdraw consent to optional processing.</p>

          <h3>7.2 California Residents (CCPA/CPRA)</h3>
          <p>California residents have additional rights: Right to Know, Right to Delete, Right to Correct, Right to Opt-Out of Sale (we do not sell data), and Right to Non-Discrimination. Contact privacy@pocketfiler.com to exercise these rights. We respond within 45 days.</p>

          <h3>7.3 Colorado Residents (Colorado Privacy Act)</h3>
          <p>Colorado residents have rights under the CPA: Right to Access, Right to Correct, Right to Delete, Right to Data Portability, and Right to Opt-Out of targeted advertising (we do not engage in this). Contact privacy@pocketfiler.com. You may appeal any decision.</p>

          <h2>8. Data Security</h2>
          <ul>
            <li><strong>Encryption in Transit:</strong> TLS/SSL for all data transmission</li>
            <li><strong>Encryption at Rest:</strong> AWS infrastructure encryption</li>
            <li><strong>Access Controls:</strong> Role-based access limiting employee access</li>
            <li><strong>Password Security:</strong> bcrypt one-way hashing</li>
            <li><strong>Infrastructure:</strong> AWS enterprise-grade security</li>
          </ul>
          <p>No system is 100% secure. We cannot guarantee absolute security.</p>

          <h2>9. Children's Privacy</h2>
          <p>PocketFiler is not intended for individuals under 18. We do not knowingly collect information from children. Contact privacy@pocketfiler.com if you believe a child has provided personal information.</p>

          <h2>10. International Data Transfers</h2>
          <p>The Platform is hosted in the United States (AWS us-east-1). By using the Platform from outside the US, you consent to data transfer to and processing in the United States.</p>

          <h2>11. Cookies</h2>
          <p>We use minimal, essential cookies: session cookies for authentication and preference cookies for language/UI settings. <strong>We do not use third-party advertising or tracking cookies.</strong></p>

          <h2>12. Changes to This Policy</h2>
          <p>Material changes will be communicated via email and/or in-app notice. Continued use after changes constitutes acceptance.</p>

          <h2>13. Contact Us</h2>
          <p>
            <strong>Privacy inquiries:</strong> privacy@pocketfiler.com<br />
            <strong>General support:</strong> support@pocketfiler.com<br />
            <strong>Address:</strong> Lucid Tech LLC, 1905 Sherman Street ST 200 1653, Denver, CO 80203
          </p>
          <p>We respond to privacy requests within 30 days (45 days for CCPA requests).</p>

          <hr />
          <p className="text-sm text-muted-foreground italic">This Privacy Policy is part of and subject to our <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>.</p>
        </div>
      </div>
    </div>
  );
}
